import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { IconButton } from "@somethings-ui/web";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { useState } from "react";
import { type TipItem, NOTE_TIPS } from "./tips";
import { DateTime } from "luxon";

interface EntityHeaderProps {
  title?: string;
  showInfo?: boolean;
  tips?: TipItem[];
  tipsSubtitle?: string;
  pagination?: {
    current: number;
    total: number;
    isLoadingNextPage?: boolean;
    onPrevious?: () => void;
    onNext?: () => void;
  };
  owner?: {
    name: string;
    avatarUrl?: string;
  };
  createdAt?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function EntityHeader({
  title,
  showInfo,
  tips = NOTE_TIPS,
  tipsSubtitle = "A few ideas to keep notes helpful",
  pagination,
  owner,
  createdAt,
  subtitle,
  actions,
}: EntityHeaderProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const formattedDate = (() => {
    if (!createdAt) return null;
    let dt = DateTime.fromISO(createdAt);
    if (!dt.isValid) {
      dt = DateTime.fromSQL(createdAt);
    }
    if (!dt.isValid) {
      dt = DateTime.fromJSDate(new Date(createdAt));
    }
    return dt.isValid ? dt.toFormat("MMM d, h:mm a") : null;
  })();

  return (
    <div className="flex w-full items-center justify-between border-b border-gray-100 px-6 py-4">
      <div className="flex items-center gap-3">
        {owner ? (
          <>
            <UserAvatar
              imageUrl={owner.avatarUrl}
              name={owner.name}
              size={40}
              showBorder
            />
            <div className="flex flex-col">
              <span className="font-semibold text-primary">{owner.name}</span>
              {(subtitle || formattedDate) && (
                <span className="text-sm text-gray-500">{subtitle ?? formattedDate}</span>
              )}
            </div>
          </>
        ) : (
          <h1 className="text-2xl font-bold leading-tight text-primary">
            {title}
          </h1>
        )}
      </div>

      <div className="flex items-center gap-2">
        {actions}
        {showInfo && (
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <IconButton
                variant="ghost-black"
                size="lg"
                icon={<Info className="size-6" />}
              />
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              sideOffset={8}
              className="w-[400px] rounded-lg bg-white p-6 shadow-lg"
            >
              <div className="flex flex-col gap-5">
                <div className="mb-4 space-y-1">
                  <h3 className="text-base font-bold text-primary">
                    Tips and tricks
                  </h3>
                  <p className="text-xs text-secondary">{tipsSubtitle}</p>
                </div>
                <div className="flex flex-col gap-5">
                  {tips.map((tip, index) => {
                    const Icon = tip.icon;
                    return (
                      <div key={index} className="flex gap-3">
                        <div className="mt-0.5 shrink-0">
                          <Icon className="size-5 text-secondary" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <h4 className="text-base text-primary">
                            {tip.title}
                          </h4>
                          <p className="text-xs text-secondary">
                            {tip.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {pagination && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {pagination.current} of {pagination.total}
            </span>
            <div className="flex flex-col">
              <button
                onClick={pagination.onPrevious}
                disabled={pagination.current === 1}
                className="flex size-4 items-center justify-center text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronUp className="size-4" />
              </button>
              <button
                onClick={pagination.onNext}
                disabled={
                  pagination.current === pagination.total ||
                  pagination.isLoadingNextPage
                }
                className="flex size-4 items-center justify-center text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronDown className="size-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
